'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { publicGet } from '@/lib/api';
import type { TeamEntry } from '../../libs/teamLabels';
import type { EventMatchSchedule } from '../../libs/eventMatchesView';

export interface EventMatchesPayload {
  matches: EventMatchSchedule[];
  luckyLoserPlayerIdsByGroup: Record<string, string[]>;
  teamEntries: Record<string, TeamEntry>;
}

/** The API response: the payload plus the stored topology revision its rows were read at. */
type EventMatchesResponse = Partial<EventMatchesPayload> & { topologyRevision?: number };

const topologyOf = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;

/**
 * Loads the event's matches (all days, one request) and exposes `reload` for
 * the refresh button and structural live updates. Only the first load is `loading`; a
 * background reload that fails keeps the rows and the last good `updatedAt`
 * and records `error`. Failed versioned loads retry slowly while visible;
 * successful loads never start a polling loop.
 *
 * A versioned reload (`reload(liveTopology)`) is dropped when the shown rows
 * already reflect that live topology revision: the payload reports the stored
 * revision it was read at (`topologyRevision`), and a `?live_topology=n` answer
 * reflects n. So the first live snapshot no longer refetches what the mount just
 * loaded; a newer revision still reloads exactly once. `reload()` with no version
 * (the refresh button) always fetches.
 */
export function useEventMatches(eventId: string) {
  const [data, setData] = useState<EventMatchesPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const refreshRef = useRef<{
    eventId: string;
    refresh: (version?: number) => Promise<void>;
  } | null>(null);
  const reload = useCallback(
    async (version?: number) => {
      if (refreshRef.current?.eventId === eventId) await refreshRef.current.refresh(version);
    },
    [eventId],
  );

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    let queuedTopology: number | null = null;
    let failedTopology: number | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    // The newest live topology the shown rows reflect; -1 until a load succeeds.
    let satisfiedTopology = -1;
    const stopRetry = () => {
      if (retryTimer !== null) clearTimeout(retryTimer);
      retryTimer = null;
    };
    // `force`: fetch even when the rows already reflect the requested topology
    // (the first load and the refresh button).
    const refresh = async (topologyVersion?: number, force = topologyVersion === undefined) => {
      if (cancelled) return;
      let version = topologyOf(topologyVersion);
      // Manual refresh also satisfies any outstanding structural hydration.
      if (failedTopology !== null) version = Math.max(version ?? 0, failedTopology);
      if (queuedTopology !== null) version = Math.max(version ?? 0, queuedTopology);
      if (!force && (version === null || version <= satisfiedTopology)) {
        // Nothing newer than the shown rows: settle any queued or failed ask.
        queuedTopology = null;
        failedTopology = null;
        stopRetry();
        return;
      }
      if (inFlight || (document.hidden && version !== null)) {
        if (version !== null) queuedTopology = Math.max(queuedTopology ?? 0, version);
        return;
      }
      queuedTopology = null;
      stopRetry();
      inFlight = true;
      try {
        const res = await publicGet<EventMatchesResponse>(
          `/event/${eventId}/matches${version === null ? '' : `?live_topology=${version}`}`,
        );
        if (cancelled) return;
        setData({
          matches: res.matches ?? [],
          luckyLoserPlayerIdsByGroup: res.luckyLoserPlayerIdsByGroup ?? {},
          teamEntries: res.teamEntries ?? {},
        });
        satisfiedTopology = Math.max(
          satisfiedTopology,
          topologyOf(res.topologyRevision) ?? 0,
          version ?? 0,
        );
        failedTopology = null;
        setError(null);
        setUpdatedAt(Date.now());
      } catch (err) {
        if (cancelled) return;
        failedTopology = version;
        setError(err instanceof Error ? err.message : 'Request failed');
      } finally {
        inFlight = false;
        if (!cancelled) {
          setLoading(false);
          // Coalesce structural changes that arrived during the request.
          if (
            queuedTopology !== null &&
            queuedTopology > Math.max(version ?? -1, satisfiedTopology)
          ) {
            void refresh(queuedTopology);
          } else {
            queuedTopology = null;
            if (failedTopology !== null && !document.hidden) {
              retryTimer = setTimeout(() => {
                retryTimer = null;
                void refresh(failedTopology ?? undefined);
              }, 60_000);
            }
          }
        }
      }
    };
    const onVisibility = () => {
      if (document.hidden) stopRetry();
      else if (failedTopology !== null || queuedTopology !== null) void refresh(undefined, false);
    };
    refreshRef.current = { eventId, refresh };
    setData(null);
    setUpdatedAt(null);
    setError(null);
    setLoading(true);
    void refresh();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      stopRetry();
      refreshRef.current = null;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [eventId]);

  return { data, loading, error, updatedAt, reload };
}

/**
 * Calls `callback` every `intervalMs` while the tab is visible; `null` turns it
 * off. Hiding the tab stops the timer, showing it again calls once immediately
 * (the data may be minutes old) and restarts — the same hidden-tab rule the
 * Schedule grid's clock follows. Switching from `null` back to a number (the
 * viewer returns to today) also refetches once at once. Mounting with a number
 * does not: the page has just loaded.
 */
export function useVisiblePolling(callback: () => void, intervalMs: number | null): void {
  const cb = useRef(callback);
  useEffect(() => {
    cb.current = callback;
  });

  // True once a running poller has been switched off, so turning it back on (the
  // viewer returns to today) refetches at once instead of waiting a minute. An
  // interval that is null before the poller has ever run (the page does not yet
  // know whether it is on today) is "not started yet", not "off".
  const hasRun = useRef(false);
  const wasOff = useRef(false);

  useEffect(() => {
    if (intervalMs === null) {
      wasOff.current = hasRun.current;
      return;
    }
    hasRun.current = true;
    if (wasOff.current) {
      wasOff.current = false;
      cb.current();
    }
    let id: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (id === null) id = setInterval(() => cb.current(), intervalMs);
    };
    const stop = () => {
      if (id !== null) clearInterval(id);
      id = null;
    };
    const onVis = () => {
      if (document.hidden) stop();
      else {
        cb.current();
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [intervalMs]);
}
